import { userSession, endSession } from 'state/session';
import { startLocalSpeechMonitor } from './speechActivity';
import { tenantConfig } from 'utils/tenant';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { onVoipPeersChanged } from './voipPeerProbe';

/**
 * Activity events that should reset the timeout timer
 */
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
  'click',
  'focus',
] as const;

/**
 * Throttle delay for activity detection (in ms)
 * This prevents excessive timer resets from rapid events like mousemove
 */
const ACTIVITY_THROTTLE_DELAY = 1000;

interface ITimeoutAgent {
  start(): void;
  stop(): void;
  reset(): void;
  isActive(): boolean;
  getRemainingTime(): number;
  showWarning(): void;
  hideWarning(): void;
}

/**
 * Session timeout management class
 * Monitors user activity and automatically ends inactive sessions
 */
class SessionTimeoutAgent implements ITimeoutAgent {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private lastActivityTime = Date.now();
  private isRunning = false;
  private warningShown = false;
  private activityThrottle = false;
  /**
   * AVS-4438: the session is live and this agent wants to monitor it. Deliberately distinct
   * from `isRunning`, which means "timers are actually armed". A standby tab is engaged but
   * never armed: it keeps watching for the moment it stops being a standby, and in the
   * meantime has no timer that could end the session.
   */
  private isEngaged = false;
  private standbyUnsubscribe: (() => void) | null = null;
  private peersUnsubscribe: (() => void) | null = null;
  private stopSpeechMonitor: (() => void) | null = null;

  constructor() {
    this.handleActivity = this.handleActivity.bind(this);
    this.handleTimeout = this.handleTimeout.bind(this);
    this.showTimeoutWarning = this.showTimeoutWarning.bind(this);
    this.syncArmedState = this.syncArmedState.bind(this);
  }

  /**
   * Start the timeout agent
   * Only dealers can start timeout monitoring as only they can end sessions
   */
  start(): void {
    if (!user.isDealer) {
      logger.debug('Timeout agent not started - user is not a dealer');
      return;
    }

    if (!tenantConfig().isAllowed('sessionTimeout')) {
      logger.debug('Timeout agent not started - timeout is disabled in tenant config');
      return;
    }

    if (this.isEngaged) {
      logger.debug('Timeout agent already running');
      return;
    }

    this.isEngaged = true;

    // AVS-4438: a tab's standby status can flip at any time, and it is derived from TWO inputs,
    // not one. `isVoipStandby` reads `voipMasters` (another tab of the same dealer claiming or
    // releasing the master slot) AND, via `voipEnabled`, `sharingType` — switching the dashboard
    // to Remote can flip a tab into standby with no `voipMasters` write at all
    // (SharingMenu writes `sharingType` on its own). Watching only `voipMasters` left a tab armed
    // as a standby, which is the recorded incident all over again. `syncArmedState` is idempotent,
    // so watching both keys costs nothing.
    this.standbyUnsubscribe = userSession().on('change', (changes: string[]) => {
      if (changes.includes('voipMasters') || changes.includes('sharingType')) this.syncArmedState();
    });
    // AVS-4485: a hand-off bystander stops being a standby when the named successor leaves the call.
    this.peersUnsubscribe = onVoipPeersChanged(this.syncArmedState);

    this.arm();
  }

  /**
   * AVS-4438: arm the timers, unless this tab is a VOIP standby.
   *
   * The 2026-09-09 field-test outage came from exactly this gap: the dealer's standby tab
   * held the only live timer, saw no DOM activity for 20 minutes because nobody was looking
   * at it, and ended the SHARED session for all 17 participants mid-call. A standby tab is by
   * definition not where the dealer is working, so it has no basis to judge them idle.
   */
  private arm(): void {
    if (!this.isEngaged || this.isRunning) return;

    if (userSession().isVoipStandby) {
      logger.debug('[Timeout] Timers not armed - this tab is a VOIP standby');
      return;
    }

    this.isRunning = true;
    this.lastActivityTime = Date.now();
    this.warningShown = false;

    this.addActivityListeners();
    this.startSpeechMonitor();

    // Start the timeout timer
    this.resetTimer();

    const sessionTimeout = tenantConfig().getSessionTimeout();
    logger.info('[Timeout] Session timeout agent started', {
      timeoutMinutes: sessionTimeout?.timeoutMinutes,
      warningMinutes: sessionTimeout?.warningMinutes,
    });
  }

  /**
   * AVS-4438: tear the timers down without disengaging, so a tab that later stops being a
   * standby can re-arm.
   */
  private disarm(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.clearTimers();
    this.removeActivityListeners();
    this.stopSpeechMonitor?.();
    this.stopSpeechMonitor = null;
    this.hideWarning();
  }

  /** AVS-4438: re-evaluate whether this tab may hold a session-ending timer. */
  private syncArmedState(): void {
    if (!this.isEngaged) return;

    if (userSession().isVoipStandby) {
      this.disarm();
      return;
    }

    if (this.isRunning) {
      // Already armed, so `arm()` would no-op. The speech monitor still needs re-evaluating:
      // its gate reads `voipEnabled`, which follows `sharingType`, so a tab that was already
      // armed when the dealer switched to Remote would otherwise never start sampling.
      this.startSpeechMonitor();
      return;
    }

    this.arm();
  }

  /**
   * AVS-4438 (D2): credit the dealer with activity while they are actually speaking.
   *
   * `ACTIVITY_EVENTS` only sees mouse, keyboard, touch and scroll, so a dealer mid-consultation
   * who is talking rather than clicking reads as idle. Speech is sampled rather than mere
   * connectivity so that a forgotten open call still runs down and gets reaped.
   */
  private startSpeechMonitor(): void {
    // Only a remote (VOIP) session has a microphone to sample. Broadcast, device and DGA
    // sessions would poll every 5s forever and never observe speech.
    if (!userSession().voipEnabled) {
      this.stopSpeechMonitor?.();
      this.stopSpeechMonitor = null;
      return;
    }

    // Idempotent on purpose. `syncArmedState` calls this on every voipMasters/sharingType change,
    // and session state churns (a takeover storm produced 977 re-entries in the field test).
    // Tearing the sampler down and rebuilding it each time would restart its 5s interval forever,
    // so a busy session would never complete a sampling window.
    if (this.stopSpeechMonitor) return;

    this.stopSpeechMonitor = startLocalSpeechMonitor(() => this.reset());
  }

  private clearTimers(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  /**
   * Stop the timeout agent and cleanup
   */
  stop(): void {
    if (!this.isEngaged && !this.isRunning) return;

    this.isEngaged = false;

    // AVS-4438: drop the standby watch, otherwise a stopped agent would re-arm itself the
    // next time another tab released the VOIP master slot.
    this.standbyUnsubscribe?.();
    this.standbyUnsubscribe = null;
    this.peersUnsubscribe?.();
    this.peersUnsubscribe = null;

    const wasRunning = this.isRunning;
    this.isRunning = false;

    this.clearTimers();

    // Remove activity listeners
    this.removeActivityListeners();

    this.stopSpeechMonitor?.();
    this.stopSpeechMonitor = null;

    // Hide any visible warnings
    this.hideWarning();

    if (wasRunning) logger.info('[Timeout] Session timeout agent stopped');
  }

  /**
   * Reset the timeout timer (called when user activity is detected)
   */
  reset(): void {
    if (!this.isRunning) return;

    this.lastActivityTime = Date.now();

    // Hide warning if it was shown
    if (this.warningShown) {
      this.hideWarning();
    }

    this.resetTimer();
  }

  /**
   * Check if the timeout agent is currently active
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * Get remaining time before timeout (in milliseconds)
   */
  getRemainingTime(): number {
    if (!this.isRunning) return 0;

    const config = tenantConfig().config;
    if (!config?.sessionTimeout) return 0;

    const timeoutDuration = config.sessionTimeout.timeoutMinutes * 60 * 1000;
    const elapsed = Date.now() - this.lastActivityTime;
    return Math.max(0, timeoutDuration - elapsed);
  }

  /**
   * Show timeout warning to the user
   */
  showWarning(): void {
    if (this.warningShown) return;

    this.warningShown = true;

    // Emit a warning event that UI components can listen to
    userSession().emit('session:timeout-warning', {
      remainingMinutes: Math.ceil(this.getRemainingTime() / (60 * 1000)),
    });

    logger.warn('[Timeout] Session timeout warning shown', {
      remainingTime: this.getRemainingTime(),
    });
  }

  /**
   * Hide timeout warning
   */
  hideWarning(): void {
    if (!this.warningShown) return;

    this.warningShown = false;

    // Emit event to hide warning
    userSession().emit('session:timeout-warning-dismissed');

    logger.debug('[Timeout] Session timeout warning dismissed');
  }

  /**
   * Add activity event listeners to the document
   */
  private addActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, this.handleActivity, {
        passive: true,
        capture: true,
      });
    });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((event) => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Handle user activity events (throttled)
   */
  private handleActivity(): void {
    if (!this.isRunning || this.activityThrottle) return;

    // Throttle activity handling to prevent excessive timer resets
    this.activityThrottle = true;
    setTimeout(() => {
      this.activityThrottle = false;
    }, ACTIVITY_THROTTLE_DELAY);

    this.reset();
  }

  /**
   * Reset the timeout and warning timers
   */
  private resetTimer(): void {
    const config = tenantConfig().config;
    if (!config?.sessionTimeout) return;

    // Clear existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }

    const timeoutMs = config.sessionTimeout.timeoutMinutes * 60 * 1000;
    const warningMs = config.sessionTimeout.warningMinutes * 60 * 1000;
    const warningDelay = timeoutMs - warningMs;

    // Set warning timer (show warning before timeout)
    this.warningTimeoutId = setTimeout(this.showTimeoutWarning, warningDelay);

    // Set timeout timer (end session)
    this.timeoutId = setTimeout(this.handleTimeout, timeoutMs);
  }

  /**
   * Show timeout warning
   */
  private showTimeoutWarning(): void {
    if (!this.isRunning) return;
    this.showWarning();
  }

  /**
   * Handle session timeout
   */
  private async handleTimeout(): Promise<void> {
    if (!this.isRunning) return;

    // AVS-4438: last line of defence. Arming is gated on `isVoipStandby`, but that value is
    // derived from session state that can change between arming and firing. Re-reading it here
    // means that even if some future input slips past `syncArmedState`, a standby tab disarms
    // instead of ending a call for everybody. This is the one place where being wrong cost 17
    // people a live consultation, so it is worth checking twice.
    if (userSession().isVoipStandby) {
      logger.warn('[Timeout] Inactivity fired on a VOIP standby tab - disarming instead of ending the session');
      this.disarm();
      return;
    }

    logger.warn('[Timeout] Session timed out due to inactivity');

    // Stop the agent to prevent multiple timeouts
    this.stop();

    try {
      // End the session with timeout flag
      await endSession({
        isTimeout: true,
        reason: 'inactivity-timeout',
        keepAlive: true,
      });
    } catch (error) {
      logger.error('[Timeout] Failed to end session on timeout', error);
    }
  }
}

// Global timeout agent instance
let timeoutAgent: SessionTimeoutAgent | null = null;

/**
 * Initialize the session timeout agent
 * This should be called when a session starts
 */
export function initSessionTimeout(): SessionTimeoutAgent {
  if (!timeoutAgent) {
    timeoutAgent = new SessionTimeoutAgent();
  }
  return timeoutAgent;
}

/**
 * Get the current timeout agent instance
 */
export function getTimeoutAgent(): SessionTimeoutAgent | null {
  return timeoutAgent;
}

/**
 * Start monitoring session timeout
 * Only starts if dealer and timeout is enabled in tenant config
 */
export function startSessionTimeout(): void {
  const agent = initSessionTimeout();
  agent.start();
}

/**
 * Stop monitoring session timeout
 */
export function stopSessionTimeout(): void {
  if (timeoutAgent) {
    timeoutAgent.stop();
  }
}

/**
 * Reset the session timeout timer (call when user activity is detected)
 */
export function resetSessionTimeout(): void {
  if (timeoutAgent) {
    timeoutAgent.reset();
  }
}

/**
 * Check if session timeout is currently active
 */
export function isSessionTimeoutActive(): boolean {
  return timeoutAgent?.isActive() ?? false;
}

/**
 * Get remaining time before session timeout (in milliseconds)
 */
export function getRemainingTimeoutTime(): number {
  return timeoutAgent?.getRemainingTime() ?? 0;
}
